import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

// 定义主题类型
export type ThemeType = 'light' | 'dark' | 'system';

// 定义主题上下文接口
interface ThemeContextType {
  theme: ThemeType;
  currentTheme: 'light' | 'dark'; // 实际应用的主题（考虑系统主题后）
  setTheme: (theme: ThemeType) => void;
  toggleTheme: () => void;
}

// 创建主题上下文
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// 主题提供者属性接口
interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * 主题提供者组件
 * 管理应用的主题状态，提供主题切换功能，并支持系统主题自动检测
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // 从本地存储读取主题或使用默认主题（强制使用light作为默认主题）
  const [theme, setThemeState] = useState<ThemeType>(() => {
    const savedTheme = localStorage.getItem('theme');
    return (savedTheme as ThemeType) || 'light';
  });
  
  // 当前实际应用的主题（考虑系统主题后）- 默认为light
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light');
  
  // 检测系统主题偏好
  const detectSystemTheme = (): 'light' | 'dark' => {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  };
  
  // 设置主题
  const setTheme = (newTheme: ThemeType) => {
    setThemeState(newTheme);
  };
  
  // 切换主题（在light/dark/system之间循环）
  const toggleTheme = () => {
    setThemeState((prevTheme) => {
      switch (prevTheme) {
        case 'light': return 'dark';
        case 'dark': return 'system';
        case 'system': return 'light';
        default: return 'light';
      }
    });
  };
  
  // 监听系统主题变化
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = () => {
      if (theme === 'system') {
        setCurrentTheme(detectSystemTheme());
      }
    };
    
    // 添加系统主题变化监听
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    } else {
      // 兼容旧版浏览器
      mediaQuery.addListener(handleSystemThemeChange);
    }
    
    // 清理监听器
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      } else {
        // 兼容旧版浏览器
        mediaQuery.removeListener(handleSystemThemeChange);
      }
    };
  }, [theme]);
  
  // 主题变化时更新本地存储和文档类名
  useEffect(() => {
    // 保存主题选择到本地存储
    localStorage.setItem('theme', theme);
    
    // 根据选择的主题设置当前主题
    if (theme === 'system') {
      setCurrentTheme(detectSystemTheme());
    } else {
      setCurrentTheme(theme);
    }
  }, [theme]);
  
  // 当前主题变化时更新文档类和CSS变量
  useEffect(() => {
    // 设置文档类名
    document.documentElement.dataset.theme = currentTheme;
    
    // 更新文档类
    if (currentTheme === 'dark') {
      document.documentElement.classList.add('dark-theme');
      document.documentElement.classList.remove('light-theme');
    } else {
      document.documentElement.classList.add('light-theme');
      document.documentElement.classList.remove('dark-theme');
    }
  }, [currentTheme]);
  
  // 组件挂载时立即应用浅色主题
  useEffect(() => {
    // 强制设置为深色主题
    document.documentElement.dataset.theme = 'dark';
    document.documentElement.classList.add('dark-theme');
    document.documentElement.classList.remove('light-theme');
    setThemeState('dark');
    setCurrentTheme('dark');
  }, []);
  
  // 提供主题上下文
  return (
    <ThemeContext.Provider value={{ theme, currentTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * 使用主题的自定义Hook
 * 获取当前主题和主题切换函数
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 